import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Title,
  Text,
  Group,
  Stack,
  Button,
  Avatar,
  Badge,
  Divider,
  ActionIcon,
  Tooltip,
  Box,
  Loader,
} from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Team, User } from '../../types';
import { formatDateShort } from '../../utils/date';
import { useTeamRoles } from '../../hooks/useReferenceApi';
import { teamRoleName } from '../../utils/teamRole';

interface TeamViewModalProps {
  opened: boolean;
  onClose: () => void;
  /** Без обработчика кнопки правки нет: на главной команду только смотрят. */
  onEdit?: () => void;
  team: Team | null;
}

interface PersonRowProps {
  user: User;
  accent?: string;
  badge?: { label: string; color: string };
  onOpenProfile: () => void;
  profileLabel: string;
}

// Одна строка человека — и в блоке руководства, и в списке участников: разница
// только в бейдже, поэтому вёрстка общая.
const PersonRow: React.FC<PersonRowProps> = ({
  user,
  accent,
  badge,
  onOpenProfile,
  profileLabel,
}) => (
  <Group gap="sm" wrap="nowrap" py={6} px="xs" style={{ borderRadius: 'var(--mantine-radius-sm)' }}>
    <Avatar
      src={user.avatar}
      radius="xl"
      size={34}
      style={accent ? { border: `2px solid ${accent}` } : undefined}
    >
      {user.name?.slice(0, 1)}
    </Avatar>
    <Box style={{ flex: 1, minWidth: 0 }}>
      <Text size="sm" fw={500} truncate>
        {user.name}
      </Text>
      <Text size="xs" c="dimmed" truncate>
        {user.position || user.email}
      </Text>
    </Box>
    {badge && (
      <Badge color={badge.color} variant="light" size="sm">
        {badge.label}
      </Badge>
    )}
    <Tooltip label={profileLabel}>
      <ActionIcon variant="subtle" color="gray" size="md" onClick={onOpenProfile}>
        <IconUser size={16} />
      </ActionIcon>
    </Tooltip>
  </Group>
);

export const TeamViewModal: React.FC<TeamViewModalProps> = ({ opened, onClose, onEdit, team }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: teamRoles = [], isLoading: isRolesLoading } = useTeamRoles();

  if (!team) return null;

  const isFunctional = team.kind === 'functional';
  // Непопулированный участник ронял бы модалку на первом обращении к полю.
  const members = (team.members || []).filter(
    (m) => m.user && m.user._id !== team.lead?._id && m.user._id !== team.pm?._id,
  );
  // Роль есть у каждого участника, поэтому она и задаёт порядок списка — в
  // порядке справочника ролей компании.
  const byRole = teamRoles
    .map((role) => ({
      role,
      people: members.filter((m) => m.role === role.key).map((m) => m.user),
    }))
    .filter((group) => group.people.length > 0);
  // Роль вне справочника (данные до миграции или роль, добавленная позже) не
  // должна прятать человека: счётчик в заголовке считает всех, и молчаливая
  // пропажа выглядела бы как потерянный участник.
  const withoutRole = members
    .filter((m) => !teamRoles.some((r) => r.key === m.role))
    .map((m) => m.user);

  const meta = [
    team.department?.name,
    team.createdAt && `${t('teams.createdAt')} ${formatDateShort(team.createdAt)}`,
    team.updatedAt && `${t('teams.updatedAt')} ${formatDateShort(team.updatedAt)}`,
  ].filter(Boolean);

  return (
    <Modal opened={opened} onClose={onClose} title={t('teams.info')} size="lg">
      <Stack gap="lg">
        <Box>
          <Group gap="xs" mb={4}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.12em' }}>
              {isFunctional ? t('teams.kindFunctional') : t('teams.kindProduct')}
            </Text>
            {team.status === 'inactive' && (
              <Badge color="gray" variant="light" size="xs">
                {t('teams.inactive')}
              </Badge>
            )}
          </Group>
          <Title order={3}>{team.name}</Title>
          {team.description && (
            <Text c="dimmed" size="sm" mt={4}>
              {team.description}
            </Text>
          )}
          {meta.length > 0 && (
            <Text size="xs" c="dimmed" mt="sm">
              {meta.join(' · ')}
            </Text>
          )}
        </Box>

        {(team.lead || team.pm) && (
          <Box>
            <Divider label={t('teams.leadership')} labelPosition="left" mb="xs" />
            {team.lead && (
              <PersonRow
                user={team.lead}
                accent="var(--mantine-color-teal-6)"
                badge={{
                  label: isFunctional ? t('teams.groupHead') : t('teams.lead'),
                  color: 'teal',
                }}
                onOpenProfile={() => navigate(`/profile/${team.lead!._id}`)}
                profileLabel={t('profile.goToProfile')}
              />
            )}
            {team.pm && (
              <PersonRow
                user={team.pm}
                accent="var(--mantine-color-grape-5)"
                badge={{ label: t('teams.pmRole'), color: 'grape' }}
                onOpenProfile={() => navigate(`/profile/${team.pm!._id}`)}
                profileLabel={t('profile.goToProfile')}
              />
            )}
          </Box>
        )}

        <Box>
          <Divider
            label={`${t('teams.membersTitle')} · ${members.length}`}
            labelPosition="left"
            mb="xs"
          />
          {/* Пока словарь грузится, людей не раскладываем: все попали бы в «без роли». */}
          {isRolesLoading ? (
            <Group justify="center" py="md">
              <Loader size="sm" />
            </Group>
          ) : members.length > 0 ? (
            <Stack gap="md">
              {byRole.map((group) => (
                <Box key={group.role.key}>
                  <Text size="xs" c="dimmed" mb={2} px="xs">
                    {teamRoleName(teamRoles, group.role.key, t)} · {group.people.length}
                  </Text>
                  {group.people.map((member) => (
                    <PersonRow
                      key={member._id}
                      user={member}
                      onOpenProfile={() => navigate(`/profile/${member._id}`)}
                      profileLabel={t('profile.goToProfile')}
                    />
                  ))}
                </Box>
              ))}
              {withoutRole.length > 0 && (
                <Box>
                  {withoutRole.map((member) => (
                    <PersonRow
                      key={member._id}
                      user={member}
                      onOpenProfile={() => navigate(`/profile/${member._id}`)}
                      profileLabel={t('profile.goToProfile')}
                    />
                  ))}
                </Box>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed" py="md" px="xs">
              {t('teams.noMembers')}
            </Text>
          )}
        </Box>

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose}>
            {t('common.close')}
          </Button>
          {onEdit && <Button onClick={onEdit}>{t('common.edit')}</Button>}
        </Group>
      </Stack>
    </Modal>
  );
};
